<template>
    <div class="oauth-callback-container">
        <div class="loading-container">
            <div class="spinner"></div>
            <h2>{{ statusMessage }}</h2>
            <p v-if="error" class="error-message">{{ error }}</p>
        </div>
    </div>
</template>

<script>
import { api } from '../utils/api'

export default {
    name: 'OAuthCallback',
    data() {
        return {
            statusMessage: 'Processing authentication...',
            error: null
        }
    },
    async created() {
        await this.handleOAuthCallback()
    },
    methods: {
        async handleOAuthCallback() {
            try {
                // Extract query parameters from URL
                const urlParams = new URLSearchParams(window.location.search)
                const code = urlParams.get('code')
                const state = urlParams.get('state')
                const error = urlParams.get('error')

                // Check for OAuth errors
                if (error) {
                    this.error = `Authentication failed: ${error}`
                    this.statusMessage = 'Authentication Error'
                    setTimeout(() => {
                        this.$router.push('/login')
                    }, 3000)
                    return
                }

                // Validate required parameters
                if (!code || !state) {
                    this.error = 'Missing authorization code or state'
                    this.statusMessage = 'Authentication Error'
                    setTimeout(() => {
                        this.$router.push('/login')
                    }, 3000)
                    return
                }

                // Get the expected state from session storage (stored before redirect)
                const expectedState = (() => {
                    try {
                        return sessionStorage.getItem('oauth_state')
                    } catch (_) {
                        return null
                    }
                })()

                if (!expectedState) {
                    this.error = 'Missing CSRF token. Please try again.'
                    this.statusMessage = 'Security Error'
                    setTimeout(() => {
                        this.$router.push('/login')
                    }, 3000)
                    return
                }

                this.statusMessage = 'Authenticating...'

                // Exchange the authorization code for an access token
                const response = await api('/auth/google/token', {
                    method: 'POST',
                    body: JSON.stringify({
                        code,
                        state,
                        expectedState
                    })
                }, this.$router, { skipAuthCheck: true })

                // Clear the stored state after use
                try {
                    sessionStorage.removeItem('oauth_state')
                } catch (_) {
                    // Ignore storage errors
                }

                // Extract access token from response
                const token = response && response.accessToken

                if (!token) {
                    throw new Error('No access token received from server')
                }

                // Store the token
                try {
                    localStorage.setItem('fic', token)
                } catch (storageError) {
                    console.error('Failed to store token:', storageError)
                    this.error = 'Failed to store authentication token. Please check browser settings.'
                    this.statusMessage = 'Storage Error'
                    return
                }

                this.statusMessage = 'Authentication successful! Redirecting...'

                setTimeout(() => {
                    window.location.replace('/dashboard')
                }, 1000)

            } catch (error) {
                console.error('OAuth callback error:', error)
                // Use code from error response if available, otherwise use default message
                this.error = error.code || 'Something went wrong please contact support'
                this.statusMessage = 'Authentication Error'

                // Redirect back to login after showing error
                setTimeout(() => {
                    this.$router.push('/login')
                }, 3000)
            }
        }
    }
}
</script>

<style scoped>
.oauth-callback-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 20px;
    background-color: var(--bg-primary);
}

.loading-container {
    max-width: 500px;
    width: 100%;
    padding: 40px 20px;
    background-color: var(--bg-secondary);
    border-radius: 8px;
    box-shadow: var(--shadow);
    border: 1px solid var(--border-color);
    text-align: center;
}

.spinner {
    width: 60px;
    height: 60px;
    border: 4px solid var(--border-color);
    border-top: 4px solid var(--accent-color);
    border-radius: 50%;
    margin: 0 auto 1.5rem;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

h2 {
    color: var(--text-primary);
    margin-bottom: 1rem;
    font-size: 1.5rem;
}

.error-message {
    color: var(--error-color);
    margin-top: 1rem;
    font-size: 0.95rem;
    line-height: 1.5;
}
</style>
